import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BusyPeriodsList } from "./BusyPeriodsList";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { startOfYear, endOfYear, format } from "date-fns";
import { ExternalLink } from "lucide-react";

export function EventsArchive() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const currentYear = new Date().getFullYear();
  const yearStart = format(startOfYear(new Date()), "yyyy-MM-dd");
  const yearEnd = format(endOfYear(new Date()), "yyyy-MM-dd");

  const { data: busyPeriods, isLoading } = useQuery({
    queryKey: ["busy_periods_current_year"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("busy_periods")
        .select("*")
        .gte("start_date", yearStart)
        .lte("start_date", yearEnd)
        .order("start_date", { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("busy_periods")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Event deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["busy_periods_current_year"] });
      queryClient.invalidateQueries({ queryKey: ["busy_periods"] });
    } catch (error) {
      console.error("Error deleting event:", error);
      toast({
        title: "Error deleting event",
        description: "Make sure you have manager permissions",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Card className="hover:shadow-lg transition-shadow duration-300">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>
          Events Archive
          <span className="block text-sm text-muted-foreground font-normal">
            Events for {currentYear}
          </span>
        </CardTitle>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/all-vacations")}
            className="gap-2"
          >
            All Vacations
            <ExternalLink className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/all-events")}
            className="gap-2"
          >
            All Events
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {busyPeriods && busyPeriods.length > 0 ? (
          <BusyPeriodsList busyPeriods={busyPeriods} handleDelete={handleDelete} />
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">
            No events for {currentYear}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
